import React from "react";
import { motion } from "framer-motion";
import { Box, Typography } from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";

const features = [
  {
    icon: GroupsRoundedIcon,
    title: "Expert Practitioners",
    description:
      "Board-certified doctors, specialists, and wellness experts with decades of experience."
  },
  {
    icon: AccessTimeRoundedIcon,
    title: "24/7 Accessibility",
    description:
      "Round-the-clock access to healthcare professionals whenever you need them."
  },
  {
    icon: SpaRoundedIcon,
    title: "Holistic Healing",
    description: "Integrated approach combining modern medicine with preventive wellness."
  },
  {
    icon: StarRoundedIcon,
    title: "Top Rated Experience",
    description: "Consistently rated 4.9+ stars by thousands of satisfied patients."
  },
  {
    icon: MemoryRoundedIcon,
    title: "Advanced Technology",
    description: "State-of-the-art health monitoring and telemedicine capabilities."
  },
  {
    icon: TrackChangesRoundedIcon,
    title: "Tailored Programs",
    description: "Personalized care plans designed specifically for your health goals."
  }
];

export default function WhyChooseUs() {
  return (
    <Box component="section" className="section-padding wellness-bg" sx={{ position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", top: 0, right: 0, width: 384, height: 384, bgcolor: "rgba(39,165,153,0.05)", borderRadius: "50%", filter: "blur(64px)" }} />
      <Box sx={{ position: "absolute", bottom: 0, left: 0, width: 384, height: 384, bgcolor: "rgba(57,172,130,0.05)", borderRadius: "50%", filter: "blur(64px)" }} />

      <Box className="container-custom" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "rgba(39,165,153,0.1)",
              color: "primary.main",
              fontSize: 14,
              fontWeight: 600,
              mb: 2
            }}
          >
            Why Choose Us
          </Box>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.15
            }}
          >
            Healthcare Designed
            <Box component="span" className="gradient-text" sx={{ display: "block" }}>
              Around You
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            We combine world-class medical expertise with personalized attention to deliver an
            unparalleled healthcare experience.
          </Typography>
        </motion.div>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 4 }}>
          {features.map((f, index) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box
                  className="glass-card"
                  sx={{
                    p: 4,
                    height: "100%",
                    transition: "all 300ms ease",
                    "&:hover": {
                      boxShadow: "var(--shadow-elevated)"
                    }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        bgcolor: "rgba(39,165,153,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 300ms ease"
                      }}
                      className="why-icon"
                    >
                      <Icon sx={{ color: "primary.main", fontSize: 24 }} className="why-icon-svg" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, mb: 1 }}>
                        {f.title}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                        {f.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
